import { MAX_SCALE, MIN_SCALE } from '../presets/scaleLimits';
import {
  applyPrimitiveResetSnapshot,
  normalizePrimitiveWithResetSnapshot,
} from '../presets/primitiveResetSnapshot';

export const CANVAS_WIDTH = 1920;
export const CANVAS_HEIGHT = 1080;

export const TEMPLATE_KEY = 'template';
const PRIMITIVE_KEY_PREFIX = 'primitive:';

export const primitiveSelectionKey = (id) => `${PRIMITIVE_KEY_PREFIX}${id}`;
export const selectionKeyToPrimitiveId = (key) => (
  typeof key === 'string' && key.startsWith(PRIMITIVE_KEY_PREFIX)
    ? key.slice(PRIMITIVE_KEY_PREFIX.length)
    : null
);

export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const pointInBounds = (x, y, bounds) => (
  Boolean(bounds) &&
  x >= bounds.minX &&
  x <= bounds.maxX &&
  y >= bounds.minY &&
  y <= bounds.maxY
);

const toRadians = (degrees) => (degrees * Math.PI) / 180;
const toDegrees = (radians) => (radians * 180) / Math.PI;
const withDefaultNumber = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};
const TEMPLATE_RESET_TRANSFORM_BASE = {
  posX: 100,
  posY: 850,
  scale: 1,
  rotation: 0,
};
const PRIMITIVE_RESET_POSITION = {
  x: 60,
  y: 40,
};

const roundTo = (value, digits = 2) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const resolveCanvasDimensions = (canvasWidth, canvasHeight) => ({
  canvasWidth: Math.max(1, withDefaultNumber(canvasWidth, CANVAS_WIDTH)),
  canvasHeight: Math.max(1, withDefaultNumber(canvasHeight, CANVAS_HEIGHT)),
});

const getTemplateResetTransform = (canvasWidth = CANVAS_WIDTH, canvasHeight = CANVAS_HEIGHT) => {
  const dims = resolveCanvasDimensions(canvasWidth, canvasHeight);
  return {
    posX: roundTo(TEMPLATE_RESET_TRANSFORM_BASE.posX * (dims.canvasWidth / CANVAS_WIDTH), 2),
    posY: roundTo(TEMPLATE_RESET_TRANSFORM_BASE.posY * (dims.canvasHeight / CANVAS_HEIGHT), 2),
    scale: TEMPLATE_RESET_TRANSFORM_BASE.scale,
    rotation: TEMPLATE_RESET_TRANSFORM_BASE.rotation,
  };
};

export const normalizeAngle = (angle) => {
  let result = angle;
  while (result > 180) result -= 360;
  while (result < -180) result += 360;
  return result;
};

const normalizeScaleMode = (mode) => (
  mode === 'x' || mode === 'y' ? mode : 'uniform'
);

const getPrimitiveTypeDefaultTransform = (type) => {
  if (type === 'line') {
    return {
      ...PRIMITIVE_RESET_POSITION,
      width: 220,
      height: 4,
      strokeWidth: 3,
      rotation: 0,
      cornerRadius: 0,
      skewX: 0,
    };
  }
  if (type === 'circle') {
    return {
      ...PRIMITIVE_RESET_POSITION,
      width: 90,
      height: 90,
      strokeWidth: 3,
      rotation: 0,
      cornerRadius: 0,
      skewX: 0,
    };
  }
  if (type === 'roundedRect') {
    return {
      ...PRIMITIVE_RESET_POSITION,
      width: 220,
      height: 80,
      strokeWidth: 3,
      rotation: 0,
      cornerRadius: 16,
      skewX: 0,
    };
  }
  if (type === 'parallelogram') {
    return {
      ...PRIMITIVE_RESET_POSITION,
      width: 220,
      height: 80,
      strokeWidth: 3,
      rotation: 0,
      cornerRadius: 0,
      skewX: 0,
    };
  }
  return {
    ...PRIMITIVE_RESET_POSITION,
    width: 220,
    height: 80,
    strokeWidth: 3,
    rotation: 0,
    cornerRadius: 0,
    skewX: 0,
  };
};

const getPrimitiveResetTransform = (shape, canvasWidth = CANVAS_WIDTH, canvasHeight = CANVAS_HEIGHT) => {
  const dims = resolveCanvasDimensions(canvasWidth, canvasHeight);
  const defaults = getPrimitiveTypeDefaultTransform(shape?.type);
  return {
    x: withDefaultNumber(shape?.resetX, defaults.x),
    y: withDefaultNumber(shape?.resetY, defaults.y),
    width: clamp(withDefaultNumber(shape?.resetWidth, defaults.width), 8, dims.canvasWidth),
    height: clamp(withDefaultNumber(shape?.resetHeight, defaults.height), 1, dims.canvasHeight),
    strokeWidth: clamp(withDefaultNumber(shape?.resetStrokeWidth, defaults.strokeWidth), 1, 80),
    rotation: clamp(withDefaultNumber(shape?.resetRotation, defaults.rotation), -180, 180),
    cornerRadius: clamp(
      withDefaultNumber(shape?.resetCornerRadius, defaults.cornerRadius),
      0,
      shape?.type === 'roundedRect' ? 540 : 999,
    ),
    skewX: withDefaultNumber(shape?.resetSkewX, defaults.skewX || 0),
  };
};

const getImagePrimitiveResetTransform = (shape, canvasWidth = CANVAS_WIDTH, canvasHeight = CANVAS_HEIGHT) => {
  const dims = resolveCanvasDimensions(canvasWidth, canvasHeight);
  return ({
  x: withDefaultNumber(shape?.resetX, withDefaultNumber(shape?.x, 60)),
  y: withDefaultNumber(shape?.resetY, withDefaultNumber(shape?.y, 40)),
  width: clamp(withDefaultNumber(shape?.resetWidth, withDefaultNumber(shape?.width, 140)), 8, dims.canvasWidth),
  height: clamp(withDefaultNumber(shape?.resetHeight, withDefaultNumber(shape?.height, 140)), 8, dims.canvasHeight),
  strokeWidth: clamp(withDefaultNumber(shape?.resetStrokeWidth, withDefaultNumber(shape?.strokeWidth, 3)), 1, 80),
  rotation: clamp(withDefaultNumber(shape?.resetRotation, withDefaultNumber(shape?.rotation, 0)), -180, 180),
  cornerRadius: clamp(
    withDefaultNumber(shape?.resetCornerRadius, shape?.imageCropMode === 'circle' ? 999 : 0),
    0,
    999,
  ),
  skewX: withDefaultNumber(shape?.resetSkewX, 0),
  });
};

const rotatePoint = (point, pivot, angleRadians) => {
  if (!angleRadians) return { ...point };
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  const sin = Math.sin(angleRadians);
  const cos = Math.cos(angleRadians);
  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos,
  };
};

export const boundsCenter = (bounds) => ({
  x: bounds.minX + bounds.width / 2,
  y: bounds.minY + bounds.height / 2,
});

const boundsWithOffset = (bounds, offsetX, offsetY) => ({
  minX: bounds.minX + offsetX,
  minY: bounds.minY + offsetY,
  maxX: bounds.maxX + offsetX,
  maxY: bounds.maxY + offsetY,
  width: bounds.width,
  height: bounds.height,
});

const unionBounds = (boundsList) => {
  const valid = boundsList.filter(Boolean);
  if (!valid.length) return null;
  const minX = Math.min(...valid.map((bounds) => bounds.minX));
  const minY = Math.min(...valid.map((bounds) => bounds.minY));
  const maxX = Math.max(...valid.map((bounds) => bounds.maxX));
  const maxY = Math.max(...valid.map((bounds) => bounds.maxY));
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const getBoundsFromPoints = (points) => {
  if (!Array.isArray(points) || !points.length) return null;
  const minX = Math.min(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxX = Math.max(...points.map((point) => point.x));
  const maxY = Math.max(...points.map((point) => point.y));
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const getBoundsCorners = (bounds) => {
  if (!bounds) return [];
  return [
    { x: bounds.minX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.maxY },
    { x: bounds.minX, y: bounds.maxY },
  ];
};

export const getAllSelectionKeys = (geometry) => {
  const keys = [];
  if (geometry?.template?.bounds) keys.push(TEMPLATE_KEY);
  if (Array.isArray(geometry?.primitives)) {
    for (const primitive of geometry.primitives) {
      if (primitive?.id) keys.push(primitiveSelectionKey(primitive.id));
    }
  }
  return keys;
};

export const sanitizeSelectionKeys = (selectionKeys, allKeys) => {
  if (!Array.isArray(allKeys) || allKeys.length === 0) return [];
  if (!Array.isArray(selectionKeys) || selectionKeys.length === 0) return [];

  const allowed = new Set(allKeys);
  const next = [];
  for (const key of selectionKeys) {
    if (!allowed.has(key)) continue;
    if (!next.includes(key)) next.push(key);
  }
  return next;
};

const toggleSelectionKey = (selectionKeys, key, allKeys) => {
  const normalized = sanitizeSelectionKeys(selectionKeys, allKeys);
  if (normalized.includes(key)) {
    const filtered = normalized.filter((item) => item !== key);
    return filtered.length ? filtered : [key];
  }
  return [...normalized, key];
};

export const resolveSelectionFromHit = ({
  currentSelection,
  hitKey,
  shiftKey,
  allKeys,
}) => {
  const normalizedCurrent = sanitizeSelectionKeys(currentSelection, allKeys);
  if (!hitKey) {
    return {
      nextSelection: shiftKey ? normalizedCurrent : [],
      shouldStartDrag: false,
      dragSelection: null,
    };
  }

  if (shiftKey) {
    return {
      nextSelection: toggleSelectionKey(normalizedCurrent, hitKey, allKeys),
      shouldStartDrag: false,
      dragSelection: null,
    };
  }

  if (normalizedCurrent.includes(hitKey)) {
    return {
      nextSelection: normalizedCurrent,
      shouldStartDrag: true,
      dragSelection: normalizedCurrent,
    };
  }

  return {
    nextSelection: [hitKey],
    shouldStartDrag: true,
    dragSelection: [hitKey],
  };
};

export const normalizeMarqueeBounds = (startPoint, endPoint) => {
  if (!startPoint || !endPoint) return null;
  const minX = Math.min(startPoint.x, endPoint.x);
  const minY = Math.min(startPoint.y, endPoint.y);
  const maxX = Math.max(startPoint.x, endPoint.x);
  const maxY = Math.max(startPoint.y, endPoint.y);
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const boundsIntersect = (a, b) => (
  Boolean(a) &&
  Boolean(b) &&
  a.minX <= b.maxX &&
  a.maxX >= b.minX &&
  a.minY <= b.maxY &&
  a.maxY >= b.minY
);

export const getSelectionKeysIntersectingBounds = (geometry, marqueeBounds) => {
  if (!geometry || !marqueeBounds) return [];
  const allKeys = getAllSelectionKeys(geometry);
  return allKeys.filter((key) => boundsIntersect(getBoundsForSelectionKey(geometry, key), marqueeBounds));
};

export const getBoundsForSelectionKey = (geometry, key) => {
  if (!geometry || !key) return null;
  if (key === TEMPLATE_KEY) return geometry.template?.bounds || null;
  const primitiveId = selectionKeyToPrimitiveId(key);
  if (!primitiveId) return null;
  const primitive = geometry.primitives?.find((item) => item.id === primitiveId);
  return primitive?.bounds || null;
};

const getLocalBoundsForSelectionKey = (geometry, key) => {
  if (!geometry || !key) return null;
  if (key === TEMPLATE_KEY) {
    return geometry.template?.localBounds
      || geometry.template?.bounds
      || null;
  }
  const primitiveId = selectionKeyToPrimitiveId(key);
  if (!primitiveId) return null;
  const primitive = geometry.primitives?.find((item) => item.id === primitiveId);
  return primitive?.localBounds || primitive?.bounds || null;
};

export const getSelectionBounds = (geometry, selectionKeys) => {
  const bounds = (selectionKeys || [])
    .map((key) => getBoundsForSelectionKey(geometry, key))
    .filter(Boolean);
  return unionBounds(bounds);
};

export const getSelectionBoundsWorld = (geometry, config, selectionKeys) => {
  const localBounds = getSelectionBounds(geometry, selectionKeys);
  if (!localBounds) return null;
  return boundsWithOffset(
    localBounds,
    withDefaultNumber(config?.posX, 0),
    withDefaultNumber(config?.posY, 0),
  );
};

const getPrimitiveRotationBySelectionKey = (config, key, fallback = 0) => {
  const primitiveId = selectionKeyToPrimitiveId(key);
  if (!primitiveId) return fallback;
  const source = Array.isArray(config?.shapePrimitives) ? config.shapePrimitives : [];
  for (let index = 0; index < source.length; index += 1) {
    const shape = source[index];
    const shapeId = shape?.id || `shape-${index}`;
    if (shapeId !== primitiveId) continue;
    return clamp(withDefaultNumber(shape?.rotation, fallback), -180, 180);
  }
  return clamp(withDefaultNumber(fallback, 0), -180, 180);
};

const getSelectionCornersWorld = (geometry, config, selectionKeys) => {
  if (!geometry || !config) return [];
  const allKeys = getAllSelectionKeys(geometry);
  const normalizedSelection = sanitizeSelectionKeys(selectionKeys, allKeys);
  if (!normalizedSelection.length) return [];

  const templateTransform = getTemplateTransform(config, geometry);
  const points = [];

  for (const key of normalizedSelection) {
    const localBounds = getLocalBoundsForSelectionKey(geometry, key);
    if (!localBounds) continue;
    const localCorners = getBoundsCorners(localBounds);

    if (key === TEMPLATE_KEY) {
      for (const corner of localCorners) {
        points.push(localToWorldPoint(corner, templateTransform));
      }
      continue;
    }

    const localCenter = boundsCenter(localBounds);
    const localRotation = getPrimitiveRotationBySelectionKey(config, key, 0);
    const localRotationRadians = toRadians(localRotation);
    for (const corner of localCorners) {
      const localRotated = rotatePoint(corner, localCenter, localRotationRadians);
      points.push(localToWorldPoint(localRotated, templateTransform));
    }
  }

  return points;
};

export const getSelectionFrameBoundsWorld = ({
  geometry,
  config,
  selectionKeys,
  frameRotateDeg = 0,
}) => {
  const selectionCornersWorld = getSelectionCornersWorld(geometry, config, selectionKeys);
  if (!selectionCornersWorld.length) return null;

  const frameRotation = withDefaultNumber(frameRotateDeg, 0);
  if (Math.abs(frameRotation) < 0.001) return getBoundsFromPoints(selectionCornersWorld);

  const origin = { x: 0, y: 0 };
  const frameRotationRadians = toRadians(frameRotation);
  const frameLocalCorners = selectionCornersWorld.map((point) => (
    rotatePoint(point, origin, -frameRotationRadians)
  ));
  const frameLocalBounds = getBoundsFromPoints(frameLocalCorners);
  if (!frameLocalBounds) return null;

  const frameLocalCenter = boundsCenter(frameLocalBounds);
  const frameWorldCenter = rotatePoint(frameLocalCenter, origin, frameRotationRadians);
  return {
    minX: frameWorldCenter.x - frameLocalBounds.width / 2,
    minY: frameWorldCenter.y - frameLocalBounds.height / 2,
    maxX: frameWorldCenter.x + frameLocalBounds.width / 2,
    maxY: frameWorldCenter.y + frameLocalBounds.height / 2,
    width: frameLocalBounds.width,
    height: frameLocalBounds.height,
  };
};

const applyAffinePoint = (point, affine) => {
  const center = affine.center || { x: 0, y: 0 };
  const scale = Number.isFinite(affine.scale) ? affine.scale : 1;
  const scaleX = Number.isFinite(affine.scaleX) ? affine.scaleX : scale;
  const scaleY = Number.isFinite(affine.scaleY) ? affine.scaleY : scale;
  const scaleAxesRotateDeg = Number.isFinite(affine.scaleAxesRotateDeg) ? affine.scaleAxesRotateDeg : 0;
  const rotateDeg = Number.isFinite(affine.rotateDeg) ? affine.rotateDeg : 0;
  const translate = affine.translate || { x: 0, y: 0 };

  const relativePoint = {
    x: point.x - center.x,
    y: point.y - center.y,
  };
  const axesRotationRadians = toRadians(scaleAxesRotateDeg);
  const localForScale = rotatePoint(relativePoint, { x: 0, y: 0 }, -axesRotationRadians);
  const scaledLocal = {
    x: localForScale.x * scaleX,
    y: localForScale.y * scaleY,
  };
  const scaledRelativePoint = rotatePoint(scaledLocal, { x: 0, y: 0 }, axesRotationRadians);
  const scaled = {
    x: center.x + scaledRelativePoint.x,
    y: center.y + scaledRelativePoint.y,
  };
  const rotated = rotatePoint(scaled, center, toRadians(rotateDeg));
  return {
    x: rotated.x + withDefaultNumber(translate.x, 0),
    y: rotated.y + withDefaultNumber(translate.y, 0),
  };
};

const localToWorldPoint = (localPoint, templateTransform) => {
  const rotated = rotatePoint(localPoint, templateTransform.pivot, toRadians(templateTransform.rotation));
  return {
    x: templateTransform.posX + rotated.x,
    y: templateTransform.posY + rotated.y,
  };
};

const worldToLocalPoint = (worldPoint, templateTransform) => {
  const shifted = {
    x: worldPoint.x - templateTransform.posX,
    y: worldPoint.y - templateTransform.posY,
  };
  return rotatePoint(shifted, templateTransform.pivot, toRadians(-templateTransform.rotation));
};

const normalizePrimitiveDraft = (shape, index) => {
  const type = shape?.type || 'rect';
  const width = Math.max(8, withDefaultNumber(shape?.width, type === 'circle' ? 90 : 220));
  const rawHeight = type === 'circle'
    ? width
    : Math.max(1, withDefaultNumber(shape?.height, type === 'line' ? 4 : 80));
  const strokeWidth = clamp(withDefaultNumber(shape?.strokeWidth, 3), 1, 80);
  const height = type === 'line' ? Math.max(strokeWidth, rawHeight) : rawHeight;
  const x = withDefaultNumber(shape?.x, 60);
  const y = withDefaultNumber(shape?.y, 40);
  const rotation = clamp(withDefaultNumber(shape?.rotation, 0), -180, 180);
  const cornerRadius = clamp(withDefaultNumber(shape?.cornerRadius, 16), 0, 540);
  const skewX = clamp(withDefaultNumber(shape?.skewX, 0), -1920, 1920);
  const id = shape?.id || `shape-${index}`;

  let center;
  if (type === 'line') {
    center = { x: x + width / 2, y };
  } else {
    center = { x: x + width / 2, y: y + height / 2 };
  }

  return {
    id,
    type,
    width,
    height,
    strokeWidth,
    cornerRadius,
    skewX,
    rotation,
    center,
  };
};

const getTemplateTransform = (config, geometry) => ({
  posX: withDefaultNumber(config?.posX, 0),
  posY: withDefaultNumber(config?.posY, 0),
  scale: clamp(withDefaultNumber(config?.scale, 1), MIN_SCALE, MAX_SCALE),
  rotation: clamp(withDefaultNumber(config?.rotation, 0), -180, 180),
  pivot: geometry?.rotationPivot || { x: 0, y: 0 },
});

const getPrimitiveStates = (config, templateTransform) => {
  const source = Array.isArray(config?.shapePrimitives) ? config.shapePrimitives : [];
  return source.map((shape, index) => {
    const normalized = normalizePrimitiveDraft(shape, index);
    return {
      index,
      key: primitiveSelectionKey(normalized.id),
      id: normalized.id,
      type: normalized.type,
      width: normalized.width,
      height: normalized.height,
      strokeWidth: normalized.strokeWidth,
      cornerRadius: normalized.cornerRadius,
      skewX: normalized.skewX,
      worldCenter: localToWorldPoint(normalized.center, templateTransform),
      worldAngle: normalized.rotation + templateTransform.rotation,
    };
  });
};

const applyPrimitiveCenter = (primitiveDraft, primitiveState, localCenter, width, height) => {
  if (primitiveState.type === 'line') {
    return {
      ...primitiveDraft,
      x: roundTo(localCenter.x - width / 2, 2),
      y: roundTo(localCenter.y, 2),
    };
  }
  return {
    ...primitiveDraft,
    x: roundTo(localCenter.x - width / 2, 2),
    y: roundTo(localCenter.y - height / 2, 2),
  };
};

export const applySelectionAffineTransform = ({
  startConfig,
  startGeometry,
  selectionKeys,
  affine,
  getGeometryForConfig,
  canvasWidth = CANVAS_WIDTH,
  canvasHeight = CANVAS_HEIGHT,
}) => {
  if (!startConfig || !startGeometry || typeof getGeometryForConfig !== 'function') return {};
  const dims = resolveCanvasDimensions(canvasWidth, canvasHeight);

  const allKeys = getAllSelectionKeys(startGeometry);
  const normalizedSelection = sanitizeSelectionKeys(selectionKeys, allKeys);
  const selectedSet = new Set(normalizedSelection);
  if (!selectedSet.size) return {};

  const baseSelectionBoundsWorld = getSelectionBoundsWorld(startGeometry, startConfig, normalizedSelection);
  const uniformScale = Math.max(0.05, withDefaultNumber(affine?.scale, 1));
  const normalizedAffine = {
    center: affine?.center || (baseSelectionBoundsWorld ? boundsCenter(baseSelectionBoundsWorld) : { x: 0, y: 0 }),
    scale: uniformScale,
    scaleX: Math.max(0.05, withDefaultNumber(affine?.scaleX, uniformScale)),
    scaleY: Math.max(0.05, withDefaultNumber(affine?.scaleY, uniformScale)),
    scaleMode: normalizeScaleMode(affine?.scaleMode),
    scaleAxesRotateDeg: withDefaultNumber(affine?.scaleAxesRotateDeg, 0),
    rotateDeg: withDefaultNumber(affine?.rotateDeg, 0),
    translate: {
      x: withDefaultNumber(affine?.translate?.x, 0),
      y: withDefaultNumber(affine?.translate?.y, 0),
    },
  };

  const startTemplate = getTemplateTransform(startConfig, startGeometry);
  const startTemplateBoundsWorld = boundsWithOffset(
    startGeometry.template?.bounds || startGeometry.renderBounds,
    startTemplate.posX,
    startTemplate.posY,
  );
  const startTemplateCenterWorld = boundsCenter(startTemplateBoundsWorld);

  let nextConfig = {
    ...startConfig,
    shapePrimitives: Array.isArray(startConfig.shapePrimitives)
      ? startConfig.shapePrimitives.map((shape) => ({ ...shape }))
      : [],
  };
  let nextGeometry = startGeometry;
  let effectiveAffine = { ...normalizedAffine };
  let templateTouched = false;
  let primitivesTouched = false;

  if (selectedSet.has(TEMPLATE_KEY)) {
    templateTouched = true;
    const nextScale = clamp(startTemplate.scale * normalizedAffine.scale, MIN_SCALE, MAX_SCALE);
    const nextRotation = normalizeAngle(startTemplate.rotation + normalizedAffine.rotateDeg);
    const appliedScale = startTemplate.scale ? nextScale / startTemplate.scale : 1;
    const appliedRotate = nextRotation - startTemplate.rotation;
    const desiredCenterWorld = applyAffinePoint(startTemplateCenterWorld, {
      ...normalizedAffine,
      scale: appliedScale,
      scaleX: appliedScale,
      scaleY: appliedScale,
      scaleMode: 'uniform',
      rotateDeg: appliedRotate,
    });

    const templateCandidate = {
      ...nextConfig,
      scale: roundTo(nextScale, 3),
      rotation: roundTo(nextRotation, 3),
    };
    let candidateGeometry = getGeometryForConfig(templateCandidate) || nextGeometry;
    const candidateTemplateBounds = candidateGeometry.template?.bounds || candidateGeometry.renderBounds;
    const candidateCenterLocal = boundsCenter(candidateTemplateBounds);

    templateCandidate.posX = roundTo(desiredCenterWorld.x - candidateCenterLocal.x, 2);
    templateCandidate.posY = roundTo(desiredCenterWorld.y - candidateCenterLocal.y, 2);

    candidateGeometry = getGeometryForConfig(templateCandidate) || candidateGeometry;
    nextConfig = {
      ...nextConfig,
      posX: templateCandidate.posX,
      posY: templateCandidate.posY,
      scale: templateCandidate.scale,
      rotation: templateCandidate.rotation,
    };
    nextGeometry = candidateGeometry;

    const finalTemplateBoundsWorld = boundsWithOffset(
      nextGeometry.template?.bounds || nextGeometry.renderBounds,
      nextConfig.posX,
      nextConfig.posY,
    );
    const finalTemplateCenterWorld = boundsCenter(finalTemplateBoundsWorld);

    const transformedWithoutTranslate = applyAffinePoint(startTemplateCenterWorld, {
      center: normalizedAffine.center,
      scale: appliedScale,
      scaleX: appliedScale,
      scaleY: appliedScale,
      scaleMode: 'uniform',
      rotateDeg: appliedRotate,
      translate: { x: 0, y: 0 },
    });

    effectiveAffine = {
      center: normalizedAffine.center,
      scale: appliedScale,
      scaleX: appliedScale,
      scaleY: appliedScale,
      scaleMode: 'uniform',
      scaleAxesRotateDeg: normalizedAffine.scaleAxesRotateDeg,
      rotateDeg: appliedRotate,
      translate: {
        x: finalTemplateCenterWorld.x - transformedWithoutTranslate.x,
        y: finalTemplateCenterWorld.y - transformedWithoutTranslate.y,
      },
    };
  }

  const nextTemplate = getTemplateTransform(nextConfig, nextGeometry);

  const primitiveStates = getPrimitiveStates(startConfig, startTemplate);
  for (const primitiveState of primitiveStates) {
    const primitiveSelected = selectedSet.has(primitiveState.key);
    const primitiveCompensated = templateTouched && !primitiveSelected;
    if (!primitiveSelected && !primitiveCompensated) continue;

    const targetCenterWorld = primitiveSelected
      ? applyAffinePoint(primitiveState.worldCenter, effectiveAffine)
      : primitiveState.worldCenter;
    const targetWorldAngle = primitiveSelected
      ? primitiveState.worldAngle + effectiveAffine.rotateDeg
      : primitiveState.worldAngle;

    const primitiveDraft = { ...nextConfig.shapePrimitives[primitiveState.index] };
    let targetWidth = primitiveState.width;
    let targetHeight = primitiveState.height;

    if (primitiveSelected) {
      const primitiveScaleX = Number.isFinite(effectiveAffine.scaleX)
        ? effectiveAffine.scaleX
        : effectiveAffine.scale;
      const primitiveScaleY = Number.isFinite(effectiveAffine.scaleY)
        ? effectiveAffine.scaleY
        : effectiveAffine.scale;
      const usesAxisScaleMode = effectiveAffine.scaleMode === 'x' || effectiveAffine.scaleMode === 'y';

      if (primitiveState.type === 'circle') {
        const sizeFactor = usesAxisScaleMode
          ? (effectiveAffine.scaleMode === 'y' ? primitiveScaleY : primitiveScaleX)
          : effectiveAffine.scale;
        const size = clamp(primitiveState.width * sizeFactor, 8, Math.max(dims.canvasWidth, dims.canvasHeight));
        targetWidth = size;
        targetHeight = size;
        primitiveDraft.width = roundTo(size, 2);
        primitiveDraft.height = roundTo(size, 2);
      } else {
        const minHeight = primitiveState.type === 'line' ? 1 : 8;
        targetWidth = clamp(primitiveState.width * primitiveScaleX, 8, dims.canvasWidth);
        targetHeight = clamp(primitiveState.height * primitiveScaleY, minHeight, dims.canvasHeight);
        primitiveDraft.width = roundTo(targetWidth, 2);
        primitiveDraft.height = roundTo(targetHeight, 2);
      }
      if (usesAxisScaleMode) {
        primitiveDraft.strokeWidth = roundTo(primitiveState.strokeWidth, 2);
        if (primitiveState.type === 'roundedRect') {
          primitiveDraft.cornerRadius = roundTo(primitiveState.cornerRadius, 2);
        }
      } else {
        primitiveDraft.strokeWidth = roundTo(
          clamp(primitiveState.strokeWidth * effectiveAffine.scale, 1, 80),
          2,
        );
        if (primitiveState.type === 'roundedRect') {
          primitiveDraft.cornerRadius = roundTo(
            clamp(primitiveState.cornerRadius * effectiveAffine.scale, 0, 540),
            2,
          );
        }
      }
      const skewScale = usesAxisScaleMode ? primitiveScaleX : effectiveAffine.scale;
      const maxSkew = Math.max(dims.canvasWidth, dims.canvasHeight);
      primitiveDraft.skewX = roundTo(
        clamp(primitiveState.skewX * skewScale, -maxSkew, maxSkew),
        2,
      );
    }

    const localCenter = worldToLocalPoint(targetCenterWorld, nextTemplate);
    const placedPrimitive = applyPrimitiveCenter(
      primitiveDraft,
      primitiveState,
      localCenter,
      targetWidth,
      targetHeight,
    );
    placedPrimitive.rotation = roundTo(
      clamp(normalizeAngle(targetWorldAngle - nextTemplate.rotation), -180, 180),
      2,
    );

    nextConfig.shapePrimitives[primitiveState.index] = placedPrimitive;
    primitivesTouched = true;
  }

  const patch = {};
  if (templateTouched) {
    patch.posX = nextConfig.posX;
    patch.posY = nextConfig.posY;
    patch.scale = nextConfig.scale;
    patch.rotation = nextConfig.rotation;
  }
  if (primitivesTouched) {
    patch.shapePrimitives = nextConfig.shapePrimitives;
  }
  return patch;
};

export const resetSelectionTransforms = ({
  startConfig,
  startGeometry,
  selectionKeys,
  getGeometryForConfig,
  canvasWidth = CANVAS_WIDTH,
  canvasHeight = CANVAS_HEIGHT,
}) => {
  if (!startConfig || !startGeometry || typeof getGeometryForConfig !== 'function') return {};
  const dims = resolveCanvasDimensions(canvasWidth, canvasHeight);

  const allKeys = getAllSelectionKeys(startGeometry);
  const normalizedSelection = sanitizeSelectionKeys(selectionKeys, allKeys);
  const selectedSet = new Set(normalizedSelection);
  if (!selectedSet.size) return {};

  const startTemplate = getTemplateTransform(startConfig, startGeometry);
  let nextConfig = {
    ...startConfig,
    shapePrimitives: Array.isArray(startConfig.shapePrimitives)
      ? startConfig.shapePrimitives.map((shape) => ({ ...shape }))
      : [],
  };
  let nextGeometry = startGeometry;
  let templateTouched = false;
  let primitivesTouched = false;

  if (selectedSet.has(TEMPLATE_KEY)) {
    templateTouched = true;
    const templateReset = getTemplateResetTransform(dims.canvasWidth, dims.canvasHeight);
    const templateCandidate = {
      ...nextConfig,
      posX: templateReset.posX,
      posY: templateReset.posY,
      scale: templateReset.scale,
      rotation: templateReset.rotation,
    };
    const candidateGeometry = getGeometryForConfig(templateCandidate) || nextGeometry;
    nextConfig = {
      ...nextConfig,
      posX: templateCandidate.posX,
      posY: templateCandidate.posY,
      scale: templateCandidate.scale,
      rotation: templateCandidate.rotation,
    };
    nextGeometry = candidateGeometry;
  }

  const nextTemplate = getTemplateTransform(nextConfig, nextGeometry);

  const primitiveStates = getPrimitiveStates(startConfig, startTemplate);
  for (const primitiveState of primitiveStates) {
    const primitiveSelected = selectedSet.has(primitiveState.key);
    const primitiveCompensated = templateTouched && !primitiveSelected;
    if (!primitiveSelected && !primitiveCompensated) continue;

    const primitiveDraft = { ...nextConfig.shapePrimitives[primitiveState.index] };
    if (primitiveSelected) {
      const normalizedPrimitive = normalizePrimitiveWithResetSnapshot(primitiveDraft);
      const hasResetSnapshot = normalizedPrimitive?.resetSnapshot && typeof normalizedPrimitive.resetSnapshot === 'object';
      const resetPrimitive = hasResetSnapshot
        ? applyPrimitiveResetSnapshot(normalizedPrimitive, normalizedPrimitive.resetSnapshot, {
          preserveLayerName: true,
          preserveVisibility: true,
        })
        : (() => {
          const reset = primitiveState.type === 'image'
            ? getImagePrimitiveResetTransform(primitiveDraft, dims.canvasWidth, dims.canvasHeight)
            : getPrimitiveResetTransform(primitiveDraft, dims.canvasWidth, dims.canvasHeight);
          return {
            ...primitiveDraft,
            width: roundTo(reset.width, 2),
            height: roundTo(reset.height, 2),
            strokeWidth: roundTo(reset.strokeWidth, 2),
            cornerRadius: reset.cornerRadius,
            skewX: roundTo(reset.skewX || 0, 2),
            x: roundTo(reset.x, 2),
            y: roundTo(reset.y, 2),
            rotation: roundTo(reset.rotation, 2),
          };
        })();
      nextConfig.shapePrimitives[primitiveState.index] = resetPrimitive;
      primitivesTouched = true;
      continue;
    }

    const targetWidth = primitiveState.width;
    const targetHeight = primitiveState.height;
    const localCenter = worldToLocalPoint(primitiveState.worldCenter, nextTemplate);
    const placedPrimitive = applyPrimitiveCenter(
      primitiveDraft,
      primitiveState,
      localCenter,
      targetWidth,
      targetHeight,
    );
    const targetWorldAngle = primitiveSelected ? nextTemplate.rotation : primitiveState.worldAngle;
    placedPrimitive.rotation = roundTo(
      clamp(normalizeAngle(targetWorldAngle - nextTemplate.rotation), -180, 180),
      2,
    );

    nextConfig.shapePrimitives[primitiveState.index] = placedPrimitive;
    primitivesTouched = true;
  }

  const patch = {};
  if (templateTouched) {
    patch.posX = nextConfig.posX;
    patch.posY = nextConfig.posY;
    patch.scale = nextConfig.scale;
    patch.rotation = nextConfig.rotation;
  }
  if (primitivesTouched) {
    patch.shapePrimitives = nextConfig.shapePrimitives;
  }
  return patch;
};

export const normalizeDragRotationDelta = (deltaRadians) => (
  normalizeAngle(toDegrees(deltaRadians))
);
